import React from 'react';
import { api } from '../../../lib/apiClient';

// WhatsApp Sender Pairing — per-user wacli account
export function WhatsAppSession({ waSession, waSessionLoading, setWaSession, setWaGroups }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Your WhatsApp account</h3>
                    <p className="text-xs text-gray-500 mt-1">
                        Pair your own phone number — it'll be used only for your notifications. No one else
                        can see this account, its groups, or send messages from it.
                    </p>
                </div>
                {waSession && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${waSession.status === 'connected'
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : (waSession.status === 'qr' || waSession.status === 'pair_requested')
                            ? 'bg-blue-50 text-blue-700 border-blue-100'
                            : 'bg-gray-100 text-gray-500 border-gray-200'
                        }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${waSession.status === 'connected' ? 'bg-green-500' : (waSession.status === 'qr' || waSession.status === 'pair_requested') ? 'bg-blue-500 animate-pulse' : 'bg-gray-400'}`}></div>
                        {waSession.status === 'connected' ? 'Connected'
                            : waSession.status === 'qr' ? 'Awaiting scan'
                            : waSession.status === 'pair_requested' ? 'Starting pairing…'
                            : waSession.status === 'stale' ? 'QR expired'
                            : 'Disconnected'}
                    </span>
                )}
            </div>

            {!waSession && waSessionLoading && (
                <div className="text-sm text-gray-400">Loading session…</div>
            )}

            {waSession?.status === 'connected' && (
                <div className="space-y-3">
                    <div className="rounded-lg bg-green-50 border border-green-100 p-4 text-sm text-green-800">
                        Your WhatsApp ({waSession.phone_number || 'unknown number'}) is paired and ready to send notifications.
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={async () => {
                                if (!confirm('Clear all pending WhatsApp notifications? Useful if a backlog has built up while you were disconnected — those messages will be dropped instead of delivered.')) return;
                                try {
                                    const res = await api.clearWhatsAppQueue();
                                    alert(`Cleared ${res?.cleared ?? 0} queued notifications.`);
                                } catch (err) {
                                    alert(`Failed to clear queue: ${err.message}`);
                                }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                        >
                            Clear pending queue
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                if (!confirm('Disconnect WhatsApp? You’ll need to scan a new QR to reconnect.')) return;
                                try {
                                    await api.unpairWhatsApp();
                                    setWaSession({ status: 'disconnected', qr_data_url: null, phone_number: null, updated_at: 0 });
                                    setWaGroups([]);
                                } catch (err) {
                                    alert(`Failed to disconnect: ${err.message}`);
                                }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50"
                        >
                            Disconnect
                        </button>
                    </div>
                </div>
            )}

            {waSession?.status === 'qr' && waSession.qr_data_url && (
                <div className="flex flex-col items-center gap-3">
                    <img
                        src={waSession.qr_data_url}
                        alt="WhatsApp QR code"
                        className="w-64 h-64 border border-gray-200 rounded-lg bg-white"
                    />
                    <p className="text-xs text-gray-500 max-w-sm text-center">
                        On your phone: WhatsApp → Settings → Linked devices → Link a device. The QR refreshes every ~20 s.
                    </p>
                </div>
            )}

            {waSession?.status === 'pair_requested' && (
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800">
                    Spinning up a wacli session for you — the QR will appear here within a few seconds.
                </div>
            )}

            {(waSession?.status === 'disconnected' || waSession?.status === 'stale' || !waSession) && (
                <div className="space-y-3">
                    <div className="rounded-lg bg-amber-50 border border-amber-100 p-4 text-sm text-amber-800">
                        {waSession?.status === 'stale'
                            ? 'The previous QR expired before being scanned. Click below to start over.'
                            : 'WhatsApp is not connected for your account. Click below to generate a pairing QR.'}
                    </div>
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                await api.pairWhatsApp();
                                setWaSession({ status: 'pair_requested', qr_data_url: null, phone_number: null, updated_at: Math.floor(Date.now()/1000) });
                            } catch (err) {
                                alert(`Failed to start pairing: ${err.message}`);
                            }
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700"
                    >
                        Connect WhatsApp
                    </button>
                </div>
            )}
        </div>
    );
}
